import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { initiatePayment, getBillingDetails, resetBillingState } from '../store/billingSlice';
import { setWalletRechargeSuccess, fetchWalletBalance, fetchWalletLedger } from '../store/walletSlice';
import { showAlert } from '../store/alertSlice';
import { initializeRazorpayPayment } from '../utils/razorpayUtils';
import { buildWalletRechargePayload } from '../pages/whatsapp/billingConfig';
import { validateRechargeCredits } from '../utils/wallet/walletUtils';

export const useWalletRechargeHandlers = ({
  setShowRechargeModal,
  pendingPaymentRef,
}) => {
  const dispatch = useDispatch();
  const billing = useSelector((state) => state.billing);
  const companyId = useSelector((state) => state.auth?.user?.companyId);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    dispatch(getBillingDetails());
  }, [dispatch]);

  useEffect(() => {
    if (!billing.success || !billing.paymentData || billing.paymentFlow !== 'wallet') {
      return;
    }

    const paymentData = billing.paymentData;
    dispatch(resetBillingState());

    initializeRazorpayPayment({
      paymentData,
      billingDetails: billing.billingDetails
        ? { email: billing.billingDetails.email, phone: billing.billingDetails.phone }
        : null,
      description: 'Wallet Recharge',
      onSuccess: () => {
        isProcessingRef.current = false;
        const pending = pendingPaymentRef.current;
        if (pending) {
          dispatch(setWalletRechargeSuccess({
            baseAmount: pending.baseAmount,
            gstAmount: pending.gstAmount,
            total: pending.total,
            paymentId: paymentData.paymentId,
          }));
          pendingPaymentRef.current = null;
        }
        // Refresh the wallet balance and transaction ledger now that credits are added.
        dispatch(fetchWalletBalance());
        dispatch(fetchWalletLedger());
        setShowRechargeModal(false);
      },
      onFailure: (error) => {
        isProcessingRef.current = false;
        pendingPaymentRef.current = null;
        dispatch(showAlert({
          message: error?.description || 'Payment failed. Please try again.',
          type: 'error',
        }));
      },
      onDismiss: () => {
        isProcessingRef.current = false;
        pendingPaymentRef.current = null;
        dispatch(showAlert({
          message: 'Payment cancelled. You can try again anytime.',
          type: 'info',
        }));
      },
    });
  }, [billing.success, billing.paymentData, billing.paymentFlow, billing.billingDetails, dispatch, pendingPaymentRef, setShowRechargeModal]);

  useEffect(() => {
    if (billing.error && billing.paymentFlow === 'wallet') {
      isProcessingRef.current = false;
      pendingPaymentRef.current = null;
      const message = typeof billing.error === 'string'
        ? billing.error
        : billing.error?.status === 400
          ? 'Please complete your billing information first.'
          : 'Payment service error. Please try again.';
      dispatch(showAlert({ message, type: 'error' }));
      dispatch(resetBillingState());
    }
  }, [billing.error, billing.paymentFlow, dispatch, pendingPaymentRef]);

  const handleInitiateWalletRecharge = (paymentSummary) => {
    if (isProcessingRef.current || billing.loading) return;

    const validation = validateRechargeCredits(paymentSummary?.baseAmount);
    if (validation.error) {
      dispatch(showAlert({ message: validation.error, type: 'error' }));
      return;
    }

    isProcessingRef.current = true;
    pendingPaymentRef.current = paymentSummary;

    const amountInPaise = Math.round(paymentSummary.total * 100);
    dispatch(initiatePayment({
      ...buildWalletRechargePayload({ companyId, amountInPaise }),
      paymentFlow: 'wallet',
    }));
  };

  return {
    handleInitiateWalletRecharge,
    isRechargeProcessing: billing.loading && billing.paymentFlow === 'wallet',
  };
};
